/**
 * SaveToListSheet Component
 *
 * Bottom sheet that shows the user's lists as checkboxes.
 * Toggling a checkbox adds/removes the item from that list.
 * Includes a "Create new list" button at the bottom.
 */

import { itemStatusQueryOptions, listsQueryOptions } from '@/api/queries/lists';
import { Modal } from '@/components/ui/modal';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, Text, View } from 'react-native';
import { useAddListItem, useRemoveListItem } from '../hooks/use-list-item-mutations';
import type { List } from '../types';
import { CreateListModal } from './create-list-modal';

interface SaveToListSheetProps {
  itemType: 'menu_item' | 'business';
  itemId: string;
  visible: boolean;
  onClose: () => void;
}

export function SaveToListSheet({ itemType, itemId, visible, onClose }: SaveToListSheetProps) {
  const [createModalVisible, setCreateModalVisible] = useState(false);

  const { data: listsData, isLoading: listsLoading } = useQuery({
    ...listsQueryOptions({ per_page: 50 }),
    enabled: visible,
  });

  const { data: statusData, isLoading: statusLoading } = useQuery({
    ...itemStatusQueryOptions(itemType, itemId),
    enabled: visible && !!itemType && !!itemId,
  });

  const addItem = useAddListItem();
  const removeItem = useRemoveListItem();

  const lists = listsData?.items ?? [];
  const savedListIds = new Set(statusData?.listIds ?? []);
  const isLoading = listsLoading || statusLoading;

  const handleToggleList = (list: List) => {
    const isInList = savedListIds.has(list.id);

    if (isInList) {
      removeItem.mutate({ listId: list.id, itemId, itemType });
    } else {
      addItem.mutate({
        listId: list.id,
        data: { item_type: itemType, item_id: itemId },
      });
    }
  };

  const handleListCreated = () => {
    // The new list will appear in the lists query after invalidation
  };

  const filteredLists = lists.filter((list) => list.name !== 'Recommended to Me');
  const defaultLists = filteredLists.filter((list) => list.type === 'default');
  const customLists = filteredLists.filter((list) => list.type === 'custom');

  const renderListRow = (list: List) => {
    const isChecked = savedListIds.has(list.id);
    const isMutating =
      (addItem.isPending && addItem.variables?.listId === list.id) ||
      (removeItem.isPending && removeItem.variables?.listId === list.id);

    return (
      <Pressable
        key={list.id}
        onPress={() => handleToggleList(list)}
        disabled={isMutating}
        className="flex-row items-center py-3 border-b border-border"
        accessibilityRole="checkbox"
        accessibilityState={{ checked: isChecked }}
        accessibilityLabel={`${list.name}, ${isChecked ? 'saved' : 'not saved'}`}
      >
        <View
          className={`w-6 h-6 rounded border items-center justify-center mr-3 ${
            isChecked ? 'border-white/40' : 'border-border'
          }`}
        >
          {isMutating ? (
            <ActivityIndicator size="small" color={isChecked ? '#fff' : undefined} />
          ) : isChecked ? (
            <Ionicons name="checkmark" size={16} className="text-white/70" />
          ) : null}
        </View>
        <View className="flex-1">
          <Text className="text-base font-medium text-foreground" numberOfLines={1}>
            {list.name}
          </Text>
          <Text className="text-xs text-muted-foreground">
            {list.itemCount} {list.itemCount === 1 ? 'item' : 'items'}
          </Text>
        </View>
      </Pressable>
    );
  };

  return (
    <>
      <Modal visible={visible} onClose={onClose}>
        <View className="bg-background rounded-xl" style={{ maxHeight: 500 }}>
          <Modal.Header>
            <View className="flex-row items-center justify-between">
              <Text className="text-lg font-bold text-foreground">
                Save to List
              </Text>
              <Pressable
                onPress={onClose}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                accessibilityRole="button"
                accessibilityLabel="Close"
              >
                <Ionicons name="close" size={24} className="text-muted-foreground" />
              </Pressable>
            </View>
          </Modal.Header>

          {isLoading ? (
            <View className="py-12 items-center">
              <ActivityIndicator />
              <Text className="mt-2 text-sm text-muted-foreground">
                Loading lists...
              </Text>
            </View>
          ) : (
            <ScrollView
              style={{ maxHeight: 320 }}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ padding: 24 }}
            >
              {filteredLists.length === 0 ? (
                <View className="items-center py-8">
                  <Text className="text-muted-foreground">
                    No lists yet. Create your first list below.
                  </Text>
                </View>
              ) : (
                <>
                  {defaultLists.length > 0 && (
                    <View className="mb-2">
                      <Text className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wider">
                        Default
                      </Text>
                      {defaultLists.map((list) => renderListRow(list))}
                    </View>
                  )}
                  {customLists.length > 0 && (
                    <View className="mb-2">
                      <Text className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wider">
                        Custom
                      </Text>
                      {customLists.map((list) => renderListRow(list))}
                    </View>
                  )}
                </>
              )}

              {/* Create new list button */}
              <Pressable
                onPress={() => setCreateModalVisible(true)}
                className="flex-row items-center py-3 mt-1"
                accessibilityRole="button"
                accessibilityLabel="Create new list"
              >
                <View className="w-6 h-6 items-center justify-center mr-3">
                  <Ionicons name="add-circle-outline" size={20} className="text-primary" />
                </View>
                <Text className="text-base font-medium text-primary">
                  Create new list
                </Text>
              </Pressable>
            </ScrollView>
          )}
        </View>
      </Modal>

      <CreateListModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onCreated={handleListCreated}
      />
    </>
  );
}
